import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Alert,
  CircularProgress,
  Divider,
  Toolbar,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  DeleteSweep as DeleteSweepIcon,
  Visibility as VisibilityIcon,
  Launch as LaunchIcon,
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  PersonAdd as PersonAddIcon,
  Campaign as CampaignIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  notificationService, 
  NotificationResponse, 
  NotificationListResponse,
  NotificationFilters 
} from '../services/notificationService';

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // State
  const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationResponse | null>(null);
  
  // Pagination & Filters
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pageSize] = useState(20);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [includeRead, setIncludeRead] = useState(true);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // UI State
  const [showFilters, setShowFilters] = useState(false);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters: NotificationFilters = {
        page: currentPage,
        size: pageSize,
        include_read: includeRead,
        priority_filter: priorityFilter || undefined,
        search: searchTerm.trim() || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      
      const response: NotificationListResponse = await notificationService.getNotifications(filters);
      
      setNotifications(response.notifications);
      setTotalPages(response.total_pages);
      setTotalCount(response.total_count);
      setUnreadCount(response.unread_count);
      
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(t('errors.notificationsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [currentPage, includeRead, priorityFilter, sortBy, sortOrder]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadNotifications();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadNotifications();
  };

  const handleSelectAll = () => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    }
  };

  const handleSelectNotification = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  };

  const handleNotificationClick = async (notification: NotificationResponse) => {
    try {
      if (!notification.is_read) {
        await notificationService.markAsRead(notification.id);
        await loadNotifications();
      }
      
      if (notification.action_url) {
        const isExternal = notification.action_type === 'external'
          || notification.action_url.startsWith('http://')
          || notification.action_url.startsWith('https://');
        if (isExternal) {
          window.open(notification.action_url, '_blank', 'noopener,noreferrer');
        } else {
          navigate(notification.action_url);
        }
      } else {
        // Show detail dialog
        setSelectedNotification(notification);
        setDetailOpen(true);
      }
    } catch (err) {
      console.error('Error handling notification click:', err);
    }
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      await notificationService.markMultipleAsRead(Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadNotifications();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      await notificationService.markMultipleAsDeleted(Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadNotifications();
    } catch (err) {
      console.error('Error deleting notifications:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      await loadNotifications();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDeleteAllRead = async () => {
    try {
      await notificationService.deleteAllRead();
      await loadNotifications();
    } catch (err) {
      console.error('Error deleting all read:', err);
    }
  };

  const getNotificationIcon = (notification: NotificationResponse) => {
    const iconName = notification.icon || 'notifications';
    const iconColor = notification.icon_color || '#2196f3';
    
    const iconStyle = { 
      fontSize: '24px', 
      color: iconColor 
    };
    
    switch (iconName) {
      case 'shopping_cart': 
        return <ShoppingCartIcon sx={iconStyle} />;
      case 'check_circle': 
        return <CheckCircleIcon sx={iconStyle} />;
      case 'local_shipping': 
        return <LocalShippingIcon sx={iconStyle} />;
      case 'receipt': 
        return <ReceiptIcon sx={iconStyle} />;
      case 'person_add': 
        return <PersonAddIcon sx={iconStyle} />;
      case 'campaign': 
        return <CampaignIcon sx={iconStyle} />;
      default: 
        return <NotificationsIcon sx={iconStyle} />;
    }
  };

  const getPriorityChipProps = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return { color: 'error' as const, label: 'URGENT' };
      case 'high':
        return { color: 'warning' as const, label: 'TINGGI' };
      case 'low':
        return { color: 'default' as const, label: 'RENDAH' };
      default:
        return { color: 'primary' as const, label: 'NORMAL' };
    }
  };

  const getMessageTypeChipProps = (messageType: string) => {
    switch (messageType.toLowerCase()) {
      case 'transactional':
        return { color: 'info' as const, label: 'Transaksi' };
      case 'promotional':
        return { color: 'secondary' as const, label: 'Promosi' };
      case 'informational':
        return { color: 'success' as const, label: 'Informasi' };
      default:
        return { color: 'default' as const, label: messageType };
    }
  };

  const formatTimeAgo = (dateString?: string): string => {
    return notificationService.getTimeAgo(dateString);
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1200px', mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Notifikasi
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary">{totalCount}</Typography>
              <Typography variant="body2" color="text.secondary">Total</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="error">{unreadCount}</Typography>
              <Typography variant="body2" color="text.secondary">Belum Dibaca</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main">{totalCount - unreadCount}</Typography>
              <Typography variant="body2" color="text.secondary">Sudah Dibaca</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4">{selectedIds.size}</Typography>
              <Typography variant="body2" color="text.secondary">Terpilih</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ mb: 2 }}>
        <Toolbar>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
            <TextField
              placeholder="Cari notifikasi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              size="small"
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />

            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
            >
              Filter
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadNotifications}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            {selectedIds.size > 0 && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<MarkEmailReadIcon />}
                  onClick={handleMarkSelectedAsRead}
                  size="small"
                >
                  Tandai Dibaca ({selectedIds.size})
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  size="small"
                >
                  Hapus ({selectedIds.size})
                </Button>
              </>
            )}

            {unreadCount > 0 && (
              <Button
                variant="contained"
                startIcon={<MarkEmailReadIcon />}
                onClick={handleMarkAllAsRead}
                size="small"
              >
                Tandai Semua Dibaca
              </Button>
            )}

            <Button
              variant="outlined"
              color="warning"
              startIcon={<DeleteSweepIcon />}
              onClick={handleDeleteAllRead}
              size="small"
            >
              Hapus Semua yang Dibaca
            </Button>
          </Box>
        </Toolbar>

        {/* Filter Options */}
        {showFilters && (
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Prioritas</InputLabel>
              <Select
                value={priorityFilter}
                label="Prioritas"
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <MenuItem value="">Semua</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="high">Tinggi</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="low">Rendah</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={includeRead ? 'all' : 'unread'}
                label="Status"
                onChange={(e) => setIncludeRead(e.target.value === 'all')}
              >
                <MenuItem value="all">Semua</MenuItem>
                <MenuItem value="unread">Belum Dibaca</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Urutkan</InputLabel>
              <Select
                value={`${sortBy}-${sortOrder}`}
                label="Urutkan"
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
              >
                <MenuItem value="created_at-desc">Terbaru</MenuItem>
                <MenuItem value="created_at-asc">Terlama</MenuItem>
                <MenuItem value="priority-desc">Prioritas Tinggi</MenuItem>
                <MenuItem value="read_status-asc">Belum Dibaca Dulu</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Notifications List */}
      <Paper>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && notifications.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Tidak ada notifikasi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || priorityFilter ? 'Coba ubah filter pencarian' : 'Belum ada notifikasi masuk'}
            </Typography>
          </Box>
        )}

        {!loading && notifications.length > 0 && (
          <>
            <List disablePadding>
              <ListItem sx={{ backgroundColor: '#f5f5f5' }}>
                <Checkbox
                  checked={selectedIds.size === notifications.length && notifications.length > 0}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < notifications.length}
                  onChange={handleSelectAll}
                />
                <ListItemText 
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Pilih Semua ({notifications.length} notifikasi)
                    </Typography>
                  }
                />
              </ListItem>
              <Divider />

              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    sx={{
                      backgroundColor: notification.is_read ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                      borderLeft: notification.is_read ? 'none' : '4px solid #1976d2',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      cursor: 'pointer'
                    }}
                  >
                    <Checkbox
                      checked={selectedIds.has(notification.id)}
                      onChange={() => handleSelectNotification(notification.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    
                    <ListItemIcon sx={{ minWidth: 56 }}>
                      {getNotificationIcon(notification)}
                    </ListItemIcon>

                    <ListItemText
                      onClick={() => handleNotificationClick(notification)}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ 
                              fontWeight: notification.is_read ? 400 : 600,
                              flexGrow: 1
                            }}
                          >
                            {notification.title}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Chip
                              {...getPriorityChipProps(notification.priority)}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            <Chip
                              {...getMessageTypeChipProps(notification.message_type)}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              mb: 0.5
                            }}
                          >
                            {notification.summary || notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimeAgo(notification.created_at)}
                            {notification.expires_at && (
                              <> • Berakhir: {formatTimeAgo(notification.expires_at)}</>
                            )}
                          </Typography>
                        </Box>
                      }
                    />

                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {notification.action_url && (
                          <Tooltip title="Buka Link">
                            <IconButton
                              size="small"
                              onClick={() => handleNotificationClick(notification)}
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Lihat Detail">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedNotification(notification);
                              setDetailOpen(true);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Hapus">
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                await notificationService.deleteNotification(notification.id);
                                await loadNotifications();
                              } catch (err) {
                                console.error('Error deleting notification:', err);
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Notification Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Detail Notifikasi</Typography>
            <IconButton onClick={() => setDetailOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        {selectedNotification && (
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedNotification.title}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  {...getPriorityChipProps(selectedNotification.priority)}
                  size="small"
                />
                <Chip
                  {...getMessageTypeChipProps(selectedNotification.message_type)}
                  size="small"
                />
                <Chip
                  label={selectedNotification.is_read ? 'Dibaca' : 'Belum Dibaca'}
                  color={selectedNotification.is_read ? 'success' : 'warning'}
                  size="small"
                />
              </Box>
            </Box>

            <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {selectedNotification.message}
            </Typography>

            <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Dibuat: {formatTimeAgo(selectedNotification.created_at)}
                {selectedNotification.read_at && (
                  <> • Dibaca: {formatTimeAgo(selectedNotification.read_at)}</>
                )}
                {selectedNotification.expires_at && (
                  <> • Berakhir: {formatTimeAgo(selectedNotification.expires_at)}</>
                )}
              </Typography>
            </Box>
          </DialogContent>
        )}

        <DialogActions>
          {selectedNotification?.action_url && (
            <Button
              variant="contained"
              startIcon={<LaunchIcon />}
              onClick={() => {
                if (selectedNotification.action_url) {
                  handleNotificationClick(selectedNotification);
                  setDetailOpen(false);
                }
              }}
            >
              {selectedNotification.action_text || 'Buka Link'}
            </Button>
          )}
          
          <Button onClick={() => setDetailOpen(false)}>
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationsPage;